import { Controller } from "@hotwired/stimulus"

// Preserves named URL params in a form submission by injecting hidden inputs
// with their current URL values before submit.
//
// Also keeps select elements inside the form in sync with the current URL
// after turbo frame renders (e.g. chip removal updates the frame but not the
// drawer, leaving selects stale). The sync sets data-preserve-url-syncing="true"
// on each select while updating so that select2-autosubmit and
// select2-value-colored controllers skip form submission during the update.
//
// Usage:
//   data-controller="ui--preserve-url-params"
//   data-ui--preserve-url-params-names-value='["grid[search]"]'
//   data-ui--preserve-url-params-frame-id-value="sources_table"
export default class extends Controller {
  static values = {
    names: Array,
    frameId: { type: String, default: "" },
  }

  connect() {
    this.element.addEventListener("submit", this.preserveParams)
    if (this.frameIdValue) {
      document.addEventListener("turbo:frame-render", this.syncSelectsFromUrl)
    }
  }

  disconnect() {
    this.element.removeEventListener("submit", this.preserveParams)
    if (this.frameIdValue) {
      document.removeEventListener("turbo:frame-render", this.syncSelectsFromUrl)
    }
  }

  preserveParams = () => {
    const urlParams = new URLSearchParams(window.location.search)
    for (const name of this.namesValue) {
      const value = urlParams.get(name) ?? ""
      const existing = this.element.querySelector(`[name="${CSS.escape(name)}"]`)
      if (existing) {
        existing.value = value
      } else {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = name
        input.value = value
        this.element.appendChild(input)
      }
    }
  }

  syncSelectsFromUrl = (event) => {
    if (event.target.id !== this.frameIdValue) return

    const urlParams = new URLSearchParams(window.location.search)
    this.element.querySelectorAll("select[name]").forEach(select => {
      const values = urlParams.getAll(select.name)

      select.dataset.preserveUrlSyncing = "true"
      Array.from(select.options).forEach(option => {
        option.selected = values.includes(option.value)
      })
      // Notify select2 to refresh its display without submitting the form
      if (window.$ && $(select).data("select2")) {
        $(select).trigger("change")
      }
      delete select.dataset.preserveUrlSyncing
    })
  }
}
